import authService from '@modules/auth/auth-service';
import Message from '@shared/message/message';
import { i18n } from '@shared/i18n';
import Errors from '@shared/error/errors';
import { routerAsync } from '@/app-module';
import ProgressBar from '@shared/progress-bar/progress-bar';
import { PropertyService } from '@modules/property/property-service';
import manageStore from '@modules/auth/auth-manage-store';

export default {
  namespaced: true,

  modules: {
    manage: manageStore
  },
  state: {
    authenticationUser: null,
    currentUser: null,
    currentProperty: null,
    loadingInit: true,
    loadingEmailConfirmation: false,
    loadingPasswordResetEmail: false,
    loadingUpdateProfile: false,
    loading: false,
  },

  getters: {
    authenticationUser: (state) => state.authenticationUser,
    currentUser: (state) => state.currentUser,
    currentProperty: (state) => state.currentProperty,
    currentUserEmail: (state, getters) =>
      getters.currentUser
        ? getters.currentUser.email
        : null,
    currentUserFullName: (state, getters) =>
      getters.currentUser
        ? getters.currentUser.fullName
        : '',

    signedIn: (state, getters) =>
      !!getters.currentUser && !!getters.currentUser.id,

    roles: (state, getters) =>
      getters.currentUser
        ? getters.currentUser.roles || []
        : [],

    emptyPermissions: (state, getters) =>
      !getters.roles || !getters.roles.length,

    loading: (state) => !!state.loading,

    loadingInit: (state) => !!state.loadingInit,

    loadingEmailConfirmation: (state) =>
      !!state.loadingEmailConfirmation,

    loadingPasswordResetEmail: (state) =>
      !!state.loadingPasswordResetEmail,

    loadingUpdateProfile: (state) =>
      !!state.loadingUpdateProfile,

    currentUserNameOrEmailPrefix: (state, getters) => {
      if (!getters.currentUser) {
        return null;
      }

      if (
        getters.currentUserFullName &&
        getters.currentUserFullName.length < 25
      ) {
        return getters.currentUserFullName;
      }

      if (getters.currentUser.firstName) {
        return getters.currentUser.firstName;
      }

      return getters.currentUser.email.split('@')[0];
    },

    currentUserAvatar: (state, getters) => {
      if (
        !getters.currentUser ||
        !getters.currentUser.avatars ||
        !getters.currentUser.avatars.length ||
        !getters.currentUser.avatars[0].publicUrl
      ) {
        return null;
      }

      return getters.currentUser.avatars[0].publicUrl;
    },
  },

  mutations: {
    CURRENT_USER_REFRESH_SUCCESS(state, payload) {
      state.currentUser = payload.currentUser || null;
    },

    AUTH_START(state) {
      state.loading = true;
    },

    AUTH_SUCCESS(state, payload) {
      state.authenticationUser =
        payload.authenticationUser || null;
      state.currentUser = payload.currentUser || null;
      state.loading = false;
    },

    AUTH_ERROR(state) {
      state.authenticationUser = null;
      state.currentUser = null;
      state.loading = false;
    },

    EMAIL_CONFIRMATION_START(state) {
      state.loadingEmailConfirmation = true;
    },

    EMAIL_CONFIRMATION_SUCCESS(state) {
      state.loadingEmailConfirmation = false;
    },

    EMAIL_CONFIRMATION_ERROR(state) {
      state.loadingEmailConfirmation = false;
    },

    PASSWORD_RESET_START(state) {
      state.loadingPasswordResetEmail = true;
    },

    PASSWORD_RESET_SUCCESS(state) {
      state.loadingPasswordResetEmail = false;
    },

    PASSWORD_RESET_ERROR(state) {
      state.loadingPasswordResetEmail = false;
    },

    UPDATE_PROFILE_START(state) {
      state.loadingUpdateProfile = true;
    },

    UPDATE_PROFILE_SUCCESS(state) {
      state.loadingUpdateProfile = false;
    },

    UPDATE_PROFILE_ERROR(state) {
      state.loadingUpdateProfile = false;
    },

    AUTH_INIT_SUCCESS(state, payload) {
      state.authenticationUser =
        payload.authenticationUser || null;
      state.currentUser = payload.currentUser || null;
      state.loadingInit = false;
    },

    AUTH_INIT_ERROR(state) {
      state.authenticationUser = null;
      state.currentUser = null;
      state.loadingInit = false;
    },

    CURRENT_PROPERTY(state, payload) {
      state.currentProperty = payload;
    },
  },

  actions: {
    async doInit({ commit, dispatch }) {
      await authService.init();

      const unsubscribe = authService.onAuthStateChanged(
        (authenticationUser) => {
          dispatch(
            'doSigninFromAuthChange',
            authenticationUser,
          );
          unsubscribe();
        },
        (error) => {
          console.error(error);
          commit('AUTH_INIT_ERROR');
        },
      );
    },

    async doWaitUntilInit({ getters }) {
      if (!getters.loadingInit) {
        return Promise.resolve();
      }

      return new Promise((resolve) => {
        const waitUntilInitInterval = setInterval(() => {

          if (!getters.loadingInit) {
            clearInterval(waitUntilInitInterval);
            resolve();
          }
        }, 500);
      });
    },

    async doSendEmailConfirmation({ commit, getters }, { originUrl }) {
      try {
        commit('EMAIL_CONFIRMATION_START');

        await authService.sendEmailVerification(
          getters.authenticationUser,
          originUrl,
        );

        Message.success(
          i18n('auth.verificationEmailSuccess'),
        );

        commit('EMAIL_CONFIRMATION_SUCCESS');
      } catch (error) {
        Errors.handle(error);
        commit('EMAIL_CONFIRMATION_ERROR');
      }
    },

    async doSendPasswordResetEmail({ commit }, { email, originUrl}) {
      try {
        commit('PASSWORD_RESET_START');
        await authService.sendPasswordResetEmail(email, originUrl);
        Message.success(i18n('auth.passwordResetEmailSuccess'));
        commit('PASSWORD_RESET_SUCCESS');
      } catch (error) {
        Errors.handle(error);
        commit('PASSWORD_RESET_ERROR');
      }
    },

    async doSigninSocial(
      { commit },
      { provider, rememberMe },
    ) {
      try {
        commit('AUTH_START');

        let authenticationUser = null;
        let currentUser = null;

        const credentials = await authService.signinWithSocial(
          provider,
          rememberMe,
        );

        if (credentials && credentials.user) {
          authenticationUser = credentials.user;
          currentUser = await authService.fetchMe();
          currentUser.emailVerified =
            authenticationUser.emailVerified;
        }

        // in background
        authService.reauthenticateWithStorageToken();


        commit('AUTH_SUCCESS', {
          currentUser,
          authenticationUser,
        });

        routerAsync().push('/');

      } catch (error) {
        await authService.signout();
        Errors.handle(error);
        commit('AUTH_ERROR');
      }
    },

    async doRegisterEmailAndPassword(
      { commit },
      { email, password },
    ) {
      try {
        commit('AUTH_START');
        const authenticationUser = await authService.registerWithEmailAndPassword(
          email,
          password,
        );
        const currentUser = await authService.fetchMe();
        currentUser.emailVerified =
          authenticationUser.emailVerified;

        // in background
        authService.reauthenticateWithStorageToken();


        commit('AUTH_SUCCESS', {
          currentUser,
          authenticationUser,
        });

        routerAsync().push('/');
      } catch (error) {
        await authService.signout();
        Errors.handle(error);
        commit('AUTH_ERROR');
      }
    },

    async doSigninWithEmailAndPassword(
      { commit, dispatch },
      { email, password, rememberMe },
    ) {
      try {
        commit('AUTH_START');

        let authenticationUser = null;
        let currentUser = null;

        const credentials = await authService.signinWithEmailAndPassword(
          email,
          password,
          rememberMe,
        );

        if (credentials && credentials.user) {
          authenticationUser = credentials.user;
          currentUser = await authService.fetchMe();
          currentUser.emailVerified =
            authenticationUser.emailVerified;
        }

        // in background
        authService.reauthenticateWithStorageToken();

        commit('AUTH_SUCCESS', {
          currentUser,
          authenticationUser,
        });

        // Change manage
        dispatch('manage/doChangeMaxManage');
        dispatch('manage/doChangeManage');
      } catch (error) {
        await authService.signout();
        Errors.handle(error);
        commit('AUTH_ERROR');
      }
    },

    async doSignout(
      { commit }, 
      { navigateOnSubmit = true }, 
      ) {
      try {
        commit('AUTH_START');
        await authService.signout();
        localStorage.removeItem('currentManage');
        localStorage.removeItem('properties');
        localStorage.removeItem('subproperties');

        commit('AUTH_SUCCESS', {
          authenticationUser: null,
          currentUser: null,
        });
        if (navigateOnSubmit) {
          routerAsync().push('/auth/signin');
        }
      } catch (error) {
        Errors.handle(error);
        commit('AUTH_ERROR');
      }
    },

    async doSigninFromAuthChange(
      { commit, dispatch },
      authenticationUser,
    ) {
      try {
        let currentUser = null;

        if (authenticationUser) {
          currentUser = await authService.fetchMe();
          authService.reauthenticateWithStorageToken();
          currentUser.emailVerified = authenticationUser.emailVerified;
          await authService.updateLastLogin();
        }

        commit('AUTH_INIT_SUCCESS', {
          currentUser,
          authenticationUser: authenticationUser,
        });

        dispatch('manage/doChangeMaxManage');
        dispatch('manage/doChangeManage', { doNavigation: false, forceFetch: true });
        
        ProgressBar.done();
      } catch (error) {
        authService.signout();
        Errors.handle(error);
        commit('AUTH_INIT_ERROR', error);
        ProgressBar.done();
      }
    },

    async doRefreshCurrentUser({ commit, getters }) {
      try {
        const authenticationUser =
          getters.authenticationUser;
        const currentUser = await authService.fetchMe();
        currentUser.emailVerified =
          authenticationUser.emailVerified;

        // in background
        //reauthenticateWithStorageToken();

        commit('CURRENT_USER_REFRESH_SUCCESS', {
          currentUser,
        });

      } catch (error) {
        authService.signout();
        Errors.handle(error);

        commit('CURRENT_USER_REFRESH_ERROR', error);
      }
    },

    async doUpdateProfile(
      { commit, dispatch, rootGetters },
      { values, navigateOnSubmit = true },
    ) {
      try {
        commit('UPDATE_PROFILE_START');

        await authService.updateProfile(
          values
        );

        const currentManage = rootGetters['auth/manage/currentManage'];

        if (currentManage && currentManage.type == 'property') {
          dispatch('property/requirements/doFetch', {
            propertyId: currentManage.property.id,
            userId: rootGetters['auth/currentUser'].id
          }, {
            root: true
          });
        } else if (currentManage && currentManage.type == 'subproperty') {
          dispatch('subproperty/requirements/doFetch', {
            propertyId: currentManage.property.id,
            subpropertyId: currentManage.subproperty.id,
            userId: rootGetters['auth/currentUser'].id
          }, {
            root: true
          });
        }

        commit('UPDATE_PROFILE_SUCCESS');
        await dispatch('doRefreshCurrentUser');
        Message.success(i18n('auth.profile.success'));
        if (navigateOnSubmit) routerAsync().push('/');
      } catch (error) {
        Errors.handle(error);
        commit('UPDATE_PROFILE_ERROR');
      }
    },

    /**
 * 
 * @param {*} param0 
 * @param {*} propertyId 
 */
    async doSetupProperty(
      { commit, dispatch }, propertyId
    ) {
      if (propertyId === '') {
        commit('CURRENT_PROPERTY', null);
      } else {
        PropertyService
          .find(propertyId)
          .then((property) => {
            commit('CURRENT_PROPERTY', property)
          })
          .catch((error) => {
            dispatch('shared/error', error, { root: true });
          })
      }
    },
  },
};
